import { useCallback, useMemo } from 'react';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { useStickyModelContentsOperation } from '../Model';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import type { ViewId } from 'balus-common';
import { useDropzone } from 'react-dropzone';
import { classNames } from '@framework/utils';
import { toast } from 'react-hot-toast';
import { useGetTransformedVisibleAreaCenterPoint } from '@user/pages/ViewModelPage';
import { useTranslation, Trans } from 'react-i18next';
import { useViewModelOperationLogSender } from '@model-framework/action-log';

type Props = {
  viewId: ViewId;
  viewRect: Rect;
  onClose(): void;
};

export const AssetUploadModal: React.FC<Props> = ({ viewId, viewRect, onClose }: Props) => {
  const { t } = useTranslation();
  const logSender = useViewModelOperationLogSender(useMemo(() => ({ viewId }), [viewId]));
  const operation = useStickyModelContentsOperation();
  const userProfile = useCurrentUserPublicProfile();
  const getTransformedVisibleAreaCenterPoint = useGetTransformedVisibleAreaCenterPoint();

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!userProfile) return;

      const createPoint = operation.getCreatePoint(getTransformedVisibleAreaCenterPoint(viewRect));
      onClose();
      const errorMessage = await operation.uploadAndAddFileContainers(files, createPoint, (fileContainerId) => {
        logSender('sticky:file_container:create', { fileContainerId, cause: 'modal' });
      });
      if (errorMessage) toast.error(errorMessage);
    },
    [operation, onClose, userProfile, getTransformedVisibleAreaCenterPoint, viewRect, logSender]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <GeneralModal
      isOpen
      onClose={onClose}
      title={t('canvas.assetUpload.title')}
      submitButton={<></>}
      cancelButton={<></>}
    >
      <div className="m-4" style={{ width: '600px', height: '300px' }}>
        <div
          {...getRootProps()}
          className={classNames(
            'h-full w-full cursor-pointer border border-dashed border-black',
            'flex items-center justify-center',
            isDragActive ? 'bg-gray-200' : ''
          )}
        >
          <span>
            <Trans i18nKey="canvas.assetUpload.dropzoneHint" components={{ lineBreak: <br /> }} />
          </span>
          <input type="file" {...getInputProps()} />
        </div>
      </div>
    </GeneralModal>
  );
};
